import React, { useState, useEffect, useRef } from 'react';
import {
  LIVE_URL_FILE_TYPE_OPTIONS,
  normalizeRootFolderOptions
} from '../liveUrlTrackingConstants';
import {
  DATE_DISPLAY_PLACEHOLDER,
  isoDateToDisplay,
  displayDateToIso,
  getTodayIsoDate,
  getDateRangeError
} from './liveUrlFilterDateUtils';
import './LiveUrlFilters.css';

const getDefaultFilters = () => {
  const today = getTodayIsoDate();
  return {
    rootFolder: [],
    pushedBy: '',
    dateFrom: today,
    dateTo: today,
    fileType: [],
    sortBy: 'pushedAt',
    sortOrder: 'desc'
  };
};

const buildLocalState = (filters) => {
  const today = getTodayIsoDate();
  const dateFrom = filters.dateFrom || today;
  const dateTo = filters.dateTo || today;

  return {
    rootFolder: filters.rootFolder || [],
    pushedBy: filters.pushedBy || '',
    dateFrom,
    dateTo,
    fileType: filters.fileType || [],
    sortBy: filters.sortBy || 'pushedAt',
    sortOrder: filters.sortOrder || 'desc',
    dateFromDisplay: isoDateToDisplay(dateFrom),
    dateToDisplay: isoDateToDisplay(dateTo)
  };
};

const LiveUrlFilters = ({
  filters,
  onFilterChange,
  rootFolderOptions = [],
  isLoading = false,
  onExport,
  isExporting = false,
  exportDisabled = false,
  totalRecords = 0,
  exportProgress = null
}) => {
  const [localFilters, setLocalFilters] = useState(() => buildLocalState(filters));
  const [usernameInput, setUsernameInput] = useState(filters.pushedBy || '');
  const [folderDropdownOpen, setFolderDropdownOpen] = useState(false);
  const [dateRangeError, setDateRangeError] = useState('');
  const [dateInputError, setDateInputError] = useState(false);
  const folderDropdownRef = useRef(null);
  const dateFromNativeRef = useRef(null);
  const dateToNativeRef = useRef(null);

  const folderOptions = normalizeRootFolderOptions(rootFolderOptions);
  const allFoldersSelected = !(localFilters.rootFolder || []).length;
  const todayMaxDate = getTodayIsoDate();

  useEffect(() => {
    setLocalFilters(buildLocalState(filters));
    setUsernameInput(filters.pushedBy || '');
  }, [filters]);

  useEffect(() => {
    setDateRangeError(getDateRangeError(localFilters.dateFrom, localFilters.dateTo));
  }, [localFilters.dateFrom, localFilters.dateTo]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (folderDropdownRef.current && !folderDropdownRef.current.contains(event.target)) {
        setFolderDropdownOpen(false);
      }
    };

    if (folderDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [folderDropdownOpen]);

  const updateLocalFilters = (updates) => {
    setLocalFilters((prev) => ({ ...prev, ...updates }));
  };

  const getRootFolderSummary = () => {
    const selected = localFilters.rootFolder || [];
    if (!selected.length) {
      return 'All Folder';
    }
    if (selected.length <= 2) {
      return selected.join(', ');
    }
    return `${selected.length} folders selected`;
  };

  const handleAllFoldersChange = () => {
    updateLocalFilters({ rootFolder: [] });
  };

  const handleRootFolderChange = (folder) => {
    const current = localFilters.rootFolder || [];
    const updated = current.includes(folder)
      ? current.filter((entry) => entry !== folder)
      : [...current, folder];
    updateLocalFilters({ rootFolder: updated });
  };

  const handleFileTypeChange = (fileType) => {
    const currentTypes = localFilters.fileType || [];
    const updatedTypes = currentTypes.includes(fileType)
      ? currentTypes.filter((type) => type !== fileType)
      : [...currentTypes, fileType];
    updateLocalFilters({ fileType: updatedTypes });
  };

  const handleDateDisplayChange = (field, displayValue) => {
    const iso = displayValue.trim() ? displayDateToIso(displayValue) : null;
    const displayField = field === 'dateFrom' ? 'dateFromDisplay' : 'dateToDisplay';

    if (displayValue.trim() && !iso) {
      setDateInputError(true);
    } else {
      setDateInputError(false);
    }

    updateLocalFilters({
      [field]: iso,
      [displayField]: displayValue
    });
  };

  const handleNativeDatePick = (field, isoValue) => {
    const displayField = field === 'dateFrom' ? 'dateFromDisplay' : 'dateToDisplay';

    if (!isoValue) {
      setDateInputError(false);
      updateLocalFilters({ [field]: null, [displayField]: '' });
      return;
    }

    setDateInputError(false);
    updateLocalFilters({
      [field]: isoValue,
      [displayField]: isoDateToDisplay(isoValue)
    });
  };

  const openNativeDatePicker = (nativeInputRef) => {
    const input = nativeInputRef.current;
    if (!input || input.disabled) return;

    if (typeof input.showPicker === 'function') {
      input.showPicker();
    } else {
      input.click();
    }
  };

  const handleUsernameClear = () => {
    setUsernameInput('');
  };

  const handleUsernameKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSearch();
    }
  };

  const buildAppliedFilters = () => ({
    rootFolder: localFilters.rootFolder || [],
    pushedBy: usernameInput.trim(),
    dateFrom: localFilters.dateFrom,
    dateTo: localFilters.dateTo,
    fileType: localFilters.fileType || [],
    sortBy: localFilters.sortBy,
    sortOrder: localFilters.sortOrder
  });

  const handleSearch = () => {
    if (dateRangeError || dateInputError) {
      return;
    }

    if (localFilters.dateFromDisplay?.trim() && !localFilters.dateFrom) {
      setDateInputError(true);
      return;
    }
    if (localFilters.dateToDisplay?.trim() && !localFilters.dateTo) {
      setDateInputError(true);
      return;
    }

    onFilterChange(buildAppliedFilters());
  };

  const handleReset = () => {
    setUsernameInput('');
    setDateInputError(false);
    setFolderDropdownOpen(false);
    const defaultFilters = getDefaultFilters();
    setLocalFilters(buildLocalState(defaultFilters));
    onFilterChange({ ...defaultFilters });
  };

  const controlsDisabled = isLoading || isExporting;
  const searchDisabled = isLoading || isExporting || !!dateRangeError || dateInputError;

  const isExportButtonDisabled = (
    isLoading
    || isExporting
    || exportDisabled
    || !!dateRangeError
    || totalRecords === 0
  );

  const getExportButtonLabel = () => {
    if (isExporting && exportProgress?.totalPages > 1) {
      return `Exporting page ${exportProgress.page} of ${exportProgress.totalPages}`;
    }
    if (isExporting) {
      return 'Exporting...';
    }
    return 'Export';
  };

  return (
    <div
      className="live-url-filters-container"
      data-testid="live-url-filters-container"
      role="region"
      aria-label="Search Criteria"
    >
      <h3 className="search-criteria-title" data-testid="search-criteria-title">
        Search Criteria
      </h3>

      <div className="filters-grid">
        <div className="filter-group">
          <label htmlFor="user-search">Username</label>
          <div className="username-input-wrapper">
            <input
              id="user-search"
              data-testid="user-search-input"
              aria-label="Username"
              type="text"
              placeholder="Search username"
              value={usernameInput}
              onChange={(e) => setUsernameInput(e.target.value)}
              onKeyDown={handleUsernameKeyDown}
              disabled={isLoading}
              style={usernameInput ? { paddingRight: '28px' } : undefined}
            />
            {usernameInput && (
              <button
                type="button"
                data-testid="user-search-clear-btn"
                aria-label="Clear username search"
                onClick={handleUsernameClear}
                disabled={isLoading}
                className="username-clear-btn"
              >
                ×
              </button>
            )}
          </div>
        </div>

        <div className="filter-group" ref={folderDropdownRef}>
          <span id="root-folder-label">Root Folder</span>
          {isLoading && <div data-testid="root-folder-loading">Loading...</div>}
          <div className="root-folder-dropdown">
            <button
              type="button"
              data-testid="root-folder-dropdown-toggle"
              className="root-folder-dropdown-toggle"
              aria-expanded={folderDropdownOpen}
              aria-haspopup="listbox"
              aria-labelledby="root-folder-label"
              onClick={() => setFolderDropdownOpen((open) => !open)}
              disabled={isLoading}
            >
              <span>{getRootFolderSummary()}</span>
              <span aria-hidden="true">▼</span>
            </button>
            {folderDropdownOpen && (
              <div
                className="root-folder-dropdown-panel file-type-multiselect"
                data-testid="root-folder-multiselect"
                role="group"
                aria-labelledby="root-folder-label"
              >
                <label className="checkbox-label" data-testid="root-folder-all">
                  <input
                    type="checkbox"
                    checked={allFoldersSelected}
                    onChange={handleAllFoldersChange}
                    disabled={isLoading}
                    aria-label="All Folder"
                  />
                  All Folder
                </label>
                {folderOptions.map((directory) => (
                  <label key={directory} className="checkbox-label">
                    <input
                      type="checkbox"
                      data-testid={`root-folder-checkbox-${directory}`}
                      checked={localFilters.rootFolder?.includes(directory) || false}
                      onChange={() => handleRootFolderChange(directory)}
                      disabled={isLoading}
                      aria-label={`Root folder ${directory}`}
                    />
                    {directory}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="filter-group">
          <label htmlFor="date-from-picker">From Date</label>
          <div className="date-input-wrapper">
            <input
              id="date-from-picker"
              data-testid="date-from-picker"
              aria-label="From Date"
              type="text"
              inputMode="numeric"
              placeholder={DATE_DISPLAY_PLACEHOLDER}
              value={localFilters.dateFromDisplay || ''}
              onChange={(e) => handleDateDisplayChange('dateFrom', e.target.value)}
              disabled={controlsDisabled}
            />
            <button
              type="button"
              data-testid="date-from-calendar-btn"
              className="date-calendar-btn"
              aria-label="Open From Date calendar"
              onClick={() => openNativeDatePicker(dateFromNativeRef)}
              disabled={controlsDisabled}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path
                  fill="currentColor"
                  d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zM5 8V6h14v2H5z"
                />
              </svg>
            </button>
            <input
              ref={dateFromNativeRef}
              data-testid="date-from-native-picker"
              type="date"
              className="date-native-picker"
              value={localFilters.dateFrom || ''}
              max={todayMaxDate}
              onChange={(e) => handleNativeDatePick('dateFrom', e.target.value)}
              tabIndex={-1}
              aria-hidden="true"
              disabled={controlsDisabled}
            />
          </div>
        </div>

        <div className="filter-group">
          <label htmlFor="date-to-picker">To Date</label>
          <div className="date-input-wrapper">
            <input
              id="date-to-picker"
              data-testid="date-to-picker"
              aria-label="To Date"
              type="text"
              inputMode="numeric"
              placeholder={DATE_DISPLAY_PLACEHOLDER}
              value={localFilters.dateToDisplay || ''}
              onChange={(e) => handleDateDisplayChange('dateTo', e.target.value)}
              disabled={controlsDisabled}
            />
            <button
              type="button"
              data-testid="date-to-calendar-btn"
              className="date-calendar-btn"
              aria-label="Open To Date calendar"
              onClick={() => openNativeDatePicker(dateToNativeRef)}
              disabled={controlsDisabled}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path
                  fill="currentColor"
                  d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zM5 8V6h14v2H5z"
                />
              </svg>
            </button>
            <input
              ref={dateToNativeRef}
              data-testid="date-to-native-picker"
              type="date"
              className="date-native-picker"
              value={localFilters.dateTo || ''}
              max={todayMaxDate}
              onChange={(e) => handleNativeDatePick('dateTo', e.target.value)}
              tabIndex={-1}
              aria-hidden="true"
              disabled={controlsDisabled}
            />
          </div>
        </div>

        <div className="filter-group filter-group-full-width">
          <label>File Types</label>
          <div className="file-type-multiselect" data-testid="file-type-multiselect">
            {LIVE_URL_FILE_TYPE_OPTIONS.map(({ value, label }) => (
              <label key={value} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={localFilters.fileType?.includes(value) || false}
                  onChange={() => handleFileTypeChange(value)}
                  disabled={isLoading}
                  aria-label={label}
                />
                {label}
              </label>
            ))}
          </div>
        </div>
      </div>

      {dateRangeError && (
        <div data-testid="date-range-error" className="error-message">
          {dateRangeError}
        </div>
      )}
      {dateInputError && (
        <div data-testid="date-input-error" className="error-message">
          Enter dates in MM/DD/YYYY format
        </div>
      )}

      <div className="filter-actions filter-actions-centered">
        <button
          type="button"
          data-testid="search-filters-btn"
          className="btn-primary"
          aria-label="Search with selected criteria"
          onClick={handleSearch}
          disabled={searchDisabled}
        >
          Search
        </button>
        <button
          type="button"
          data-testid="export-tracking-btn"
          className={isExportButtonDisabled ? 'btn-secondary' : 'btn-primary'}
          disabled={isExportButtonDisabled}
          onClick={onExport}
          aria-label="Export filtered records to Excel"
        >
          {getExportButtonLabel()}
        </button>
        <button
          type="button"
          data-testid="reset-filters-btn"
          className="btn-secondary"
          disabled={isLoading || isExporting}
          onClick={handleReset}
        >
          Reset
        </button>
        {totalRecords === 0 && !isLoading && (
          <span
            data-testid="export-empty-message"
            className="export-empty-message"
            aria-live="polite"
          >
            No records to export
          </span>
        )}
      </div>
    </div>
  );
};

export default LiveUrlFilters;
